// Complete CV data for Anis MNASRI

export const personalInfo = {
  name: "Anis MNASRI",
  title: {
    en: "QA / Test Automation Lead (SDET)",
    fr: "Ingénieur QA / Lead (SDET)"
  },
  contact: {
    phone: "+33 (6) 12 60 35 05",
    email: "anis.mnsri@gmail.com",
    github: "https://github.com/anismnasri",
    linkedin: "https://linkedin.com/in/anismnasri"
  },
  photoPlaceholder: true
};

export const profile = {
  en: "I have 18 years of experience in software testing and quality assurance, including 7 years as a QA / Test Automation Lead on embedded systems, SaaS platforms, and Web & Mobile applications. I'm used to working in Agile environments, setting up QA infrastructures, automating test processes, and improving test strategies over time. I also have solid experience leading teams and making sure the software delivered to users is reliable and high quality.",
  fr: "18 ans d'expérience en test et assurance qualité logicielle, y compris 7 ans en tant que Lead Automatisation et QA logiciel, sur des systèmes embarqués, des solutions SaaS et des applications Web et Mobile. Expertise dans la gestion des tests dans un contexte Agile, l'automatisation des processus, la mise en place d'infrastructures QA, et l'amélioration des stratégies de test. Aptitude démontrée à diriger des équipes et assurer la qualité des logiciels."
};

export const education = {
  title: {
    en: "Education",
    fr: "Formation"
  },
  degree: {
    en: "Master's degree in Electronics and Instrumentation",
    fr: "Maîtrise en Électronique et Instrumentation"
  },
  school: "École Supérieure des Sciences et Techniques de Tunis - Tunisie"
};

export const technicalSkills = {
  title: {
    en: "Technical Skills",
    fr: "Compétences Techniques"
  },
  categories: [
    {
      name: {
        en: "Languages & Scripting",
        fr: "Langages & Scripting"
      },
      skills: ["Python", "Core Java", "JavaScript", "C#", "Bash", "PowerShell", "Expect"]
    },
    {
      name: {
        en: "AI-driven Testing",
        fr: "Test Logique & AI"
      },
      skills: ["Prompt Engineering", "Dynamic Test Generation", "FastAPI + GPT-x"]
    },
    {
      name: {
        en: "Data & Configuration",
        fr: "Données & Configuration"
      },
      skills: ["JSON", "YAML", "XML", "CSV", ".env", "Maven POM"]
    },
    {
      name: {
        en: "Web E2E Testing",
        fr: "Test Web E2E"
      },
      skills: ["Selenium WebDriver", "Playwright", "Robot Framework", "Pytest"]
    },
    {
      name: {
        en: "Mobile Testing",
        fr: "Test Mobile"
      },
      skills: ["Appium", "BrowserStack", "Android", "iOS"]
    },
    {
      name: {
        en: "API Testing",
        fr: "Test API"
      },
      skills: ["Postman", "Newman", "Apickli"]
    },
    {
      name: {
        en: "Performance Testing",
        fr: "Test de Performance"
      },
      skills: ["k6", "JMeter", "Gatling", "InfluxDB", "Grafana"]
    },
    {
      name: {
        en: "DVB / IPTV Tools",
        fr: "Outils DVB / IPTV"
      },
      skills: ["DVBsnoop", "Wireshark", "TSReader", "MediaStream"]
    },
    {
      name: {
        en: "CI/CD",
        fr: "CI/CD"
      },
      skills: ["Jenkins", "GitLab CI/CD", "Azure DevOps"]
    },
    {
      name: {
        en: "Monitoring",
        fr: "Monitoring"
      },
      skills: ["Grafana", "Firebase Crashlytics", "New Relic", "Android Studio"]
    },
    {
      name: {
        en: "Test Management",
        fr: "Gestion des Tests"
      },
      skills: ["Jira", "Xray", "IBM CLM", "Azure Boards", "Bugzilla"]
    }
  ]
};

export const functionalSkills = {
  title: {
    en: "Functional Skills",
    fr: "Compétences Fonctionnelles"
  },
  skills: {
    en: [
      "Build and drive the overall test strategy: by sprint, release, or functional module",
      "Identify quality risks, sensitive areas, and define non-regression scope (manual and automated)",
      "Work closely with Product, Design, Dev, QA and Ops teams",
      "Review User Stories from a QA point of view (3 Amigos style): clarity, value, testability, consistency",
      "Quality follow-up & reporting: monitor requirement coverage by test cases, and prioritise defects based on user and business impact"
    ],
    fr: [
      "Élaborer la stratégie de tests : validation fonctionnelle par sprint, release ou module fonctionnel",
      "Identification des risques qualité, des périmètres sensibles et définition du périmètre de non-régression (tests manuels ou auto)",
      "Collaboration transverse avec les équipes produit, design, dev, QA et Ops",
      "Revue des User Stories sous angle QA (type 3 Amigos) : Clarté, valeur, testabilité, cohérence",
      "Suivi Qualité & reporting fonctionnel : Suivi des taux de couverture des exigences fonctionnelles par les cas de test et prioriser les anomalies en fonction de l'impact utilisateur et business"
    ]
  }
};

export const experience = {
  title: {
    en: "Professional Experience",
    fr: "Expérience Professionnelle"
  },
  positions: [
    {
      period: "November 2021 – Present",
      company: "Le Parisien",
      role: {
        en: "QA Lead Automation",
        fr: "Lead QA Automatisation"
      },
      highlights: {
        en: [
          "Define and lead the global test strategy: Web, Mobile (Android / iOS), APIs, gateways, back-office",
          "Build and manage the quality roadmap in line with business and technical priorities",
          "Design and evolve the test automation architecture in a CI/CD environment (Playwright, Robot Framework, Appium, Postman, Apickli)",
          "Industrialise QA processes with a strong focus on product, user impact and delivered value",
          "Integrate an in-house AI analysis tool to enrich test feedback",
          "Run quality audits: accessibility, performance, UX from a QA perspective",
          "Contribute to technology watch: explore new solutions, build POCs / MVPs",
          "Provide support on customer incidents: analyse subscriber feedback, app store reviews"
        ],
        fr: [
          "Définition et pilotage de la stratégie de test globale : Web, Mobile (Android / iOS), API, Gateway, Back Office",
          "Élaboration et gestion de la roadmap qualité en lien avec les enjeux métiers et techniques",
          "Mise en place et évolution de l'architecture de tests auto dans un environnement CI/CD (Playwright, Robot Framework, Appium, Postman, Apickli)",
          "Industrialisation des processus QA avec une vision orientée produit, utilisateur et valeur",
          "Intégration d'outil d'analyse IA (outil maison) pour enrichir les retours de test",
          "Réalisation d'audits qualité : accessibilité, performance, UX QA",
          "Contribution à la veille technologique : exploration de nouvelles solutions, POC/ MVP",
          "Support client et incident : analyse des retours abonnés / non abonnés, avis Store"
        ]
      }
    },
    {
      period: "May 2021 – October 2021",
      company: "GrAI Matter Labs",
      role: {
        en: "QA & DevOps Manager",
        fr: "Responsable QA & DevOps"
      },
      highlights: {
        en: [
          "Created QA team from scratch and integrated QA practices into the software development lifecycle",
          "Defined the test strategy based on internal needs and customer expectations",
          "Deployed and configured test management tools",
          "Designed an automation framework to cover manual development and deployment tasks for code and data",
          "Led interviews and hiring to build a dedicated QA team (recruitment of two QA engineers)",
          "Defined the team's scope, monitored daily status, and drove continuous progress and delivery",
          "Tech: Linux Debian 10, GitLab, Docker, xStudio, Python 3.9, C, SoC, FPGA, Raspberry Pi"
        ],
        fr: [
          "Création de l'équipe QA et intégration du processus au sein du flow du cycle de développement logiciel",
          "Définir la stratégie de test selon les besoins internes et des clients",
          "Mise en place des outils de management de tests",
          "Design du Framework automatique qui couvre toutes les tâches manuelles de développement et de déploiement de code et de données",
          "Faire les entrevues de candidatures afin de créer l'équipe QA dédiée (recrutement de deux ingénieurs)",
          "Définir les axes d'intervention de l'équipe, suivre le statut au quotidien et améliorer l'avancement",
          "Env. technique : Linux Debian 10, GitLab, Docker, xStudio, Python 3.9, C, SoC, FPGA, Raspberry Pi"
        ]
      }
    },
    {
      period: "September 2020 – May 2021",
      company: "Suez",
      role: {
        en: "QA Lead Automation",
        fr: "Lead QA Automatisation"
      },
      highlights: {
        en: [
          "Created and deployed automation environment for web applications around remote meter reading",
          "Improved the existing test strategy and strengthened testing activities for the MOA (business) team",
          "Supported the MOA team by structuring a testing workflow via test standards defined in Azure DevOps",
          "Designed the architecture of a C# console application for GUI non-regression tests based on Selenium WebDriver",
          "Promoted BDD best practices using SpecFlow (Gherkin)",
          "Installed CI environments with Jenkins and Azure DevOps",
          "Contributed to load and performance testing project for a 'Big Data' Kafka migration (delivered by Atos)",
          "Tech: Windows 10, Azure DevOps, Git, Visual Studio, Selenium WebDriver, SpecFlow, C#, Postman, Jenkins, MySQL, PostgreSQL"
        ],
        fr: [
          "Création et mise en place de l'environnement de l'automatisation de l'ensemble d'applications Web autour de la télérelève",
          "Améliorer la stratégie de tests existante et renforcer les aspects de tests pour l'équipe MOA",
          "Suivre l'équipe MOA et les assister autour d'un workflow de tests selon un ensemble de standard de tests sous Azure DevOps",
          "Architecture d'une application console C# pour les tests de non-régression IHM en se basant sur Selenium WebDriver",
          "Bonne pratique BDD SpecFlow (Gherkin)",
          "Installation d'un environnement de CI sous Jenkins et Azure DevOps",
          "Intervenir sur un projet fourni par Atos, sur les tests de charge et de performance, pour une migration Big Data Kafka",
          "Env. technique : Windows 10, Azure DevOps, Git, Visual Studio, Selenium WebDriver, SpecFlow, C#, Postman, Jenkins, MySQL, PostgreSQL"
        ]
      }
    },
    {
      period: "November 2019 – September 2020",
      company: "ISAAC Instruments",
      role: {
        en: "QA Lead Automation",
        fr: "Lead QA Automatisation"
      },
      highlights: {
        en: [
          "Created and deployed automation environment for connected trucking and telemetry applications (SaaS solution)",
          "Tested and validated the full ISAAC application ecosystem: client/server fleet management, data analysis, real-time vehicle tracking",
          "Participated in defining the overall test strategy and QA scope",
          "Analyzed the Canadian ELD (Electronic Logging Device) standard for 2020 and derived test cases",
          "Designed and built automated non-regression test system: framework based on Appium/Selenium WebDriver (Java)",
          "Architected and industrialized continuous integration: installed and administered Jenkins/Azure DevOps",
          "Integrated Postman tests into Jenkins using the Newman CLI",
          "Trained the functional validation team on automation practices and tooling",
          "Tech: Windows 10, Azure DevOps, Git, Confluence, Eclipse, Appium, Selenium WebDriver, Maven/POM, TestNG, Postman, Jenkins"
        ],
        fr: [
          "Création et mise en place de l'environnement de l'automatisation de l'ensemble d'applications autour de l'internet des camionnages et télémétrie (solution SaaS)",
          "Test et validation de l'ensemble des applications ISAAC : gestion de parc de véhicules, analyse de données, communication temps réel pour suivi véhicules",
          "Participer à la mise en place de la stratégie de tests, définition de périmètre d'intervention QA",
          "Analyse fonctionnelle de la standard ELD Canadien pour 2020 et dériver les cas de tests à partir des exigences Transport Canada",
          "Design et Création d'un Framework de tests basé sur Appium/Selenium WebDriver, écrit en Java",
          "Architecture et industrialiser l'Intégration continue : installation et administration de Jenkins/Azure DevOps",
          "Intégrer les tests Postman à Jenkins en se basant sur le mode CLI Newman de Postman",
          "Former l'équipe de validation fonctionnelle sur les moyens de l'automatisation",
          "Env. technique : Windows 10, Azure DevOps, Git, Confluence, Eclipse, Appium, Selenium WebDriver, Maven/POM, TestNG, Postman, Jenkins"
        ]
      }
    },
    {
      period: "April 2013 – December 2018",
      company: "HARMAN by Samsung",
      role: {
        en: "Senior QA Engineer",
        fr: "Senior Ingénieur QA"
      },
      highlights: {
        en: [
          "Testing, validation and tooling for a native Type-1 hypervisor dedicated to Android smartphones and automotive use cases",
          "Took ownership of deliverable quality from development through integration to production, in an Agile Scrum context",
          "Designed test scenarios, test plans and documentation based on automotive standards",
          "Contributed to Safety ISO 26262 ASIL B certification work",
          "Built Python 2.7 libraries from scratch for system testing of virtualised OSes: Linux, Android, QNX",
          "Maintained CI (Jenkins) and test framework with a suite of 500+ UI tests for native and virtualised Android",
          "Developed automated and semi-automated tests in Python and Java",
          "Tech: Linux, Eclipse (Java/PyDev), Git, Jenkins, Doxygen, Confluence"
        ],
        fr: [
          "Test, validation et outils – hyperviseur de type Natif-1 : la virtualisation dédiée aux smartphones Android et à l'automotive",
          "Assurer la responsabilité de la qualité des livrables de la phase de développement, d'intégration au déploiement en production en méthodologie Agile SCRUM",
          "Conception de scénarios de tests, plan de tests et documentation sur des normes de l'automotive",
          "Participation à la certification Safety ISO 26262 ASIL B",
          "Implémentation des librairies from scratch en Python 2.7 pour des fins de tests systèmes OS virtualisé : Linux, Android, QNX",
          "Piloter un framework de tests avec une batterie de 500 tests UI pour Android natif et virtualisé sous un environnement Linux",
          "Développement de test (automatique, semi-automatique) en Python / Java",
          "Env. technique : Linux, Eclipse Java/PyDev, GIT, Jenkins, Doxygen, Confluence"
        ]
      }
    },
    {
      period: "March 2012 – December 2012",
      company: "OpenTV",
      role: {
        en: "Embedded Software QA Engineer",
        fr: "Ingénieur QA logiciel Embarqué"
      },
      highlights: {
        en: [
          "UPC IPTV Set-Top Box testing and validation",
          "Reviewed technical and functional documentation",
          "Analyzed requirements and produced a high-level test strategy and macro planning (Mind Map approach)",
          "Defined and wrote test plans based on specifications and requirements",
          "Performed certification and broadcast standard tests (Bertin, Viaccess, etc.)",
          "Tested Nagra access control and analyzed issues together with the client",
          "Tech: UPC STB, Wireshark, MediaTab, MediaStream, Stream Express, XMind"
        ],
        fr: [
          "Set Top Box UPC IPTV - test et validation",
          "Étude des documents techniques et fonctionnels",
          "Analyse et compréhension des besoins et livraison d'une stratégie de test macro avec un macro-planning (méthode MindMap)",
          "Définir / élaborer les plans de tests à partir des spécifications et l'ensemble des exigences",
          "Test de certifications et standard de diffusion (Bertin, Viaccess, ...)",
          "Test de contrôle d'accès Nagra et analyse et diagnostic des anomalies avec le client",
          "Env. technique : STB UPC, WireShark, MediaTab, MediaStream, Stream Express, XMind"
        ]
      }
    },
    {
      period: "January 2013 – February 2013",
      company: "Schneider Electric",
      role: {
        en: "Integration Engineer",
        fr: "Ingénieur Intégration"
      },
      highlights: {
        en: [
          "Testing and integration of the P51 High and Medium Voltage Protection platform",
          "Refactored code to comply with MISRA C++ 2008 and C 2004 standards",
          "Performed structural and unit tests",
          "Executed static analysis on firmware and reported test results",
          "Tech: C/C++, VxWorks OS, Workbench, Visual Studio, SVN, ClearCase, Doxygen"
        ],
        fr: [
          "Test et Intégration de la plateforme P51 Protection Haute et moyen Tension",
          "Réécriture du code selon les règles standards de MISRA C++ 2008 et C 2004",
          "Test structurel et unitaire",
          "Test et analyse statique du Firmware, rapports des résultats et gestion de bugs",
          "Env. technique : C/C++, VxWorks OS, Workbench, Visual Studio, SVN, ClearCase, Doxygen"
        ]
      }
    },
    {
      period: "January 2019 – November 2019",
      company: "Magillem Design Services",
      role: {
        en: "Senior QA Engineer / Developer",
        fr: "Sénior Ingénieur QA / Développement"
      },
      highlights: {
        en: [
          "Testing and validation of multiple solutions in the context of EDA (Electronic Design Automation) projects",
          "Defined validation test cases to implement and automate, together with the Product Owner and development team",
          "Qualified software outputs (GUI and command-line): generated HDL files, C functions, documentation according to IP-XACT standard",
          "Documented tests and ensured traceability of test runs with Xray",
          "Analyzed existing tools and processes and proposed improvements as a basis for an ISO 26262 ASIL-C certification campaign",
          "Tech: Jenkins, Git, GitLab, Xray, Jira, Confluence"
        ],
        fr: [
          "Test, validation de multiples solutions autour du contexte des projets EDA (Electronic Design Automation)",
          "Définir les cas de tests de validation à implémenter et automatiser avec le PO et l'équipe de Développement",
          "Qualifier les sorties du logiciel graphique ou en mode commande : fichiers HDL générés, fonctions C, documentation selon les règles de la standard IP-XACT",
          "Documentation et traçabilités des tests et des exécutions avec Xray",
          "Analyser et étudier les moyens de bord existants et comment les améliorer pour démarrer une campagne de certification ISO-26262 ASIL-C",
          "Env. technique : Jenkins, Git, GitLab, Xray, Jira, Confluence"
        ]
      }
    },
    {
      period: "September 2007 – February 2012",
      company: "Sagemcom",
      role: {
        en: "Software Test & Integration Engineer",
        fr: "Ingénieur Test et Intégration Logiciel"
      },
      highlights: {
        en: [
          "ETI90 HD Set-Top Box (DVB-T2): On-site mission in London for BBC product demos and DTG certification contribution",
          "Supported and coached the validation team on testing needs (tools, environments, release notes)",
          "Canal Digital Norway Set-Top Box (IPTV): Wrote test plans, performed Viaccess access control and Bertin specification tests",
          "Conducted functional tests: UI, PVR, audio/video, Dolby AC3/AC3+, VOD, interoperability tests (HDMI, DHCP)",
          "HAL-3 & DLI: Installed validation environments on Linux, customized Linux kernel and integrated Sagemcom drivers",
          "Tech: ST5100/ST7105 platform, Sagem STBs, ST Micro Connect, Spooler, Hex Editor, StreamExpress"
        ],
        fr: [
          "Set Top Box ETI90 HD (DVB-T2) : Mission client à Londres pour démonstration produit BBC et étude certification DTG",
          "Support et coaching de l'équipe de validation pour les besoins de tests (outils, environnement, release note)",
          "Set Top Box Canal Digital Norvège (IPTV) : Rédaction des plans de tests, tests de contrôle d'accès Viaccess et spécification Bertin",
          "Tests fonctionnels : IHM, PVR, audio/vidéo, Dolby AC3/AC3+, VOD, tests d'interopérabilité HDMI et Protocol DHCP",
          "HAL-3 & DLI : Installation d'environnement de validation HAL3 sous Linux, customisation du noyau Linux et intégration des drivers Sagemcom",
          "Env. technique : Platform ST5100/ST7105, STB SAGEM, ST Micro Connect, Spooler, Hex Editor, StreamExpress"
        ]
      }
    }
  ]
};

export const projects = {
  title: {
    en: "Projects & Achievements",
    fr: "Projets & Réalisations"
  },
  items: [
    {
      title: {
        en: "AI-Powered Test Generation Tool",
        fr: "Outil de Génération de Tests IA"
      },
      description: {
        en: "Designed and developed a test generation tool using FastAPI and GPT-x for dynamic test case creation through prompt engineering.",
        fr: "Conception et développement d'un outil de génération de tests utilisant FastAPI et GPT-x pour la création dynamique de cas de test via prompt engineering."
      },
      tech: ["FastAPI", "Python", "GPT-x", "Prompt Engineering"]
    },
    {
      title: {
        en: "Multi-Platform Automation Framework",
        fr: "Framework d'Automatisation Multi-Plateforme"
      },
      description: {
        en: "Built comprehensive automation framework covering Web (Playwright), Mobile (Appium), and API testing with CI/CD integration.",
        fr: "Construction d'un framework d'automatisation complet couvrant les tests Web (Playwright), Mobile (Appium) et API avec intégration CI/CD."
      },
      tech: ["Playwright", "Appium", "Robot Framework", "Jenkins", "Azure DevOps"]
    },
    {
      title: {
        en: "Performance Monitoring System",
        fr: "Système de Monitoring de Performance"
      },
      description: {
        en: "Implemented performance testing infrastructure with k6, integrated with InfluxDB and Grafana for real-time monitoring and reporting.",
        fr: "Mise en place d'infrastructure de tests de performance avec k6, intégrée à InfluxDB et Grafana pour monitoring et reporting en temps réel."
      },
      tech: ["k6", "InfluxDB", "Grafana", "JMeter"]
    },
    {
      title: {
        en: "ISO 26262 Certification Contribution",
        fr: "Contribution Certification ISO 26262"
      },
      description: {
        en: "Key contributor to safety certification work for automotive-grade software, achieving ASIL B compliance.",
        fr: "Contributeur clé aux travaux de certification sécurité pour logiciels automobiles, conformité ASIL B atteinte."
      },
      tech: ["ISO 26262", "ASIL B", "Safety Testing", "Documentation"]
    }
  ]
};
